(() => {
  'use strict';

  const VERSION = '2026-09-28.1-explicit-preview-cart';
  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const BLOB_API_URL = 'https://vercel.com/api/blob/';
  const BLOB_API_VERSION = '12';
  const TEEINBLUE_PROPERTY_NAMES = [
    'properties[_customization_id]',
    'properties[_customization_image]',
    'properties[_tib_design_link_1]'
  ];
  const instances = new WeakMap();

  const isHttpsUrl = value => {
    try { return new URL(String(value || '')).protocol === 'https:'; } catch { return false; }
  };

  const isUuid = value => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ''));

  const jsonFetch = async (url, options) => {
    const response = await fetch(url, options);
    let body = null;
    try { body = await response.json(); } catch {}
    if (!response.ok) {
      const code = body?.error || body?.message || `HTTP ${response.status}`;
      throw new Error(code);
    }
    return body;
  };

  const decodeStoreId = delegationToken => {
    const segment = String(delegationToken || '').split('.')[0];
    if (!segment) throw new Error('invalid_upload_authorization');
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(segment.length / 4) * 4, '=');
    const payload = JSON.parse(atob(base64));
    const storeId = String(payload.storeId || '').replace(/^store_/, '');
    if (!storeId) throw new Error('invalid_upload_authorization');
    return storeId;
  };

  const addPresignedParams = (url, payload) => {
    const target = new URL(url);
    Object.entries(payload.params || {}).forEach(([key, value]) => target.searchParams.set(key, value));
    target.searchParams.set('vercel-blob-delegation', payload.delegationToken);
    target.searchParams.set('vercel-blob-signature', payload.signature);
    return target;
  };

  // Minimal, self-contained equivalent of @vercel/blob@2.8.0 uploadPresigned().
  // No long-lived Blob credential is present in the theme; the server provides a short-lived grant.
  const uploadPresigned = async ({ apiUrl, pathname, file, metadata, signal }) => {
    const tokenResponse = await jsonFetch(`${apiUrl}/api/pms-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'blob.generate-presigned-url',
        payload: {
          pathname,
          clientPayload: JSON.stringify(metadata),
          multipart: false
        }
      }),
      signal
    });
    const presigned = tokenResponse?.presignedUrlPayload;
    if (!presigned?.delegationToken || !presigned?.signature) throw new Error('upload_token_failed');

    const storeId = decodeStoreId(presigned.delegationToken);
    const uploadUrl = new URL(BLOB_API_URL);
    uploadUrl.searchParams.set('pathname', pathname);
    const response = await fetch(addPresignedParams(uploadUrl, presigned), {
      method: 'PUT',
      body: file,
      headers: {
        'x-api-blob-request-id': `${storeId}:${Date.now()}:${Math.random().toString(16).slice(2)}`,
        'x-vercel-blob-store-id': storeId,
        'x-api-blob-request-attempt': '0',
        'x-api-version': BLOB_API_VERSION,
        'x-vercel-blob-access': 'public',
        'x-content-type': file.type
      },
      signal
    });
    let blob = null;
    try { blob = await response.json(); } catch {}
    if (!response.ok) throw new Error(blob?.error?.message || `Blob upload failed (${response.status})`);
    return blob;
  };

  class PmsUploadController {
    constructor(section) {
      this.section = section;
      this.form = section.querySelector('.fp-product-form');
      this.upload = section.querySelector('[data-pms-upload]');
      this.input = section.querySelector('[data-pms-file-input]');
      this.preview = section.querySelector('[data-pms-local-preview]');
      this.previewImage = section.querySelector('[data-pms-local-preview-image]');
      this.status = section.querySelector('[data-pms-status]');
      this.statusText = section.querySelector('[data-pms-status-text]');
      this.progress = section.querySelector('[data-pms-progress]');
      this.progressBar = section.querySelector('[data-pms-progress-bar]');
      this.progressPercent = section.querySelector('[data-pms-progress-percent]');
      this.error = section.querySelector('[data-pms-error]');
      this.retry = section.querySelector('[data-pms-retry]');
      this.generate = section.querySelector('[data-pms-generate]');
      this.readyCard = section.querySelector('[data-pms-ready-card]');
      this.mobileView = section.querySelector('[data-pms-mobile-view]');
      this.viewButtons = Array.from(section.querySelectorAll('[data-pms-view-preview]'));
      this.buttons = Array.from(section.querySelectorAll('[data-fp-add-to-cart], [data-fp-checkout]'));
      this.abortController = null;
      this.listenerController = new AbortController();
      this.buttonObserver = null;
      this.progressFrame = 0;
      this.progressValue = 0;
      this.revision = 0;
      this.objectUrl = '';
      this.lastFile = null;
      this.generating = false;
      this.submitting = false;
      this.state = { generationId: '', originalUrl: '', previewUrl: '' };
      this.apiUrl = String(section.dataset.pmsApiBaseUrl || '').replace(/\/$/, '');
      this.mode = String(section.dataset.pmsMode || 'car');
      this.expectedBlobHostname = String(section.dataset.pmsBlobHostname || '');
      const storageSuffix = `${section.dataset.pmsProductId || ''}:${window.location.pathname}`;
      this.storageKey = `pms:self-upload:v2:${storageSuffix}`;
      this.legacyStorageKey = `pms:self-upload:v1:${storageSuffix}`;
    }

    mount() {
      if (!this.form || !this.upload || !this.input || !this.apiUrl) return;
      this.section.dataset.pmsUploadController = VERSION;
      const signal = this.listenerController.signal;
      this.input.addEventListener('change', event => this.onFileChange(event), { signal });
      this.upload.querySelector('[data-pms-reupload]')?.addEventListener('click', () => {
        this.resetForNewUpload();
        this.input.click();
      }, { signal });
      this.generate?.addEventListener('click', () => {
        if (this.lastFile && !this.generating && !this.ready()) this.start(this.lastFile);
      }, { signal });
      this.retry?.addEventListener('click', () => {
        this.resetProgress();
        window.setTimeout(() => {
          if (isHttpsUrl(this.state.previewUrl)) {
            this.markPreviewReady(this.state.previewUrl, this.revision);
          } else if (this.lastFile) this.start(this.lastFile);
          else this.input.click();
        }, 0);
      }, { signal });
      this.viewButtons.forEach(button => button.addEventListener('click', () => this.submitPreviewToCart(), { signal }));
      window.addEventListener('click', event => this.interceptPurchase(event), { capture: true, signal });
      window.addEventListener('submit', event => this.interceptPurchase(event), { capture: true, signal });
      window.addEventListener('pageshow', () => {
        this.submitting = false;
        this.syncPurchaseGate();
        if (this.state.previewUrl) this.markPreviewReady(this.state.previewUrl, this.revision);
      }, { signal });
      this.section.addEventListener('change', () => window.setTimeout(() => {
        this.syncPurchaseGate();
      }, 0), { signal });

      this.buttonObserver = new MutationObserver(() => this.syncPurchaseGate());
      this.buttons.forEach(button => this.buttonObserver.observe(button, { attributes: true, attributeFilter: ['disabled'] }));
      this.disableTeeinblueProperties();
      this.removeGeneratedMedia();
      try { sessionStorage.removeItem(this.legacyStorageKey); } catch {}
      if (!this.restore()) this.syncPurchaseGate();
    }

    destroy() {
      this.revision += 1;
      this.abortController?.abort();
      this.listenerController.abort();
      this.buttonObserver?.disconnect();
      this.stopProgressAnimation();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
    }

    onFileChange(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      const validationError = this.validateFile(file);
      if (validationError) {
        this.showError(validationError, false);
        return;
      }
      this.lastFile = file;
      this.revision += 1;
      this.abortController?.abort();
      this.abortController = null;
      this.generating = false;
      this.clearRemoteState();
      this.showLocalPreview(file);
      this.hideError();
      this.status.hidden = true;
      this.generate.hidden = false;
      this.syncPurchaseGate();
    }

    validateFile(file) {
      if (!ALLOWED_TYPES.has(file.type)) return 'Please choose a JPEG, PNG or WebP image.';
      if (file.size > MAX_FILE_BYTES) return 'Your photo is larger than 10 MB. Please choose a smaller file.';
      if (file.size <= 0) return 'The selected image is empty. Please choose another file.';
      return '';
    }

    showLocalPreview(file) {
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(file);
      this.previewImage.src = this.objectUrl;
      this.previewImage.alt = 'Your uploaded photo preview';
      this.preview.dataset.pmsPreviewKind = 'original';
      this.preview.hidden = false;
      this.section.querySelector('[data-pms-upload-trigger]').hidden = true;
    }

    resetForNewUpload() {
      this.revision += 1;
      this.abortController?.abort();
      this.abortController = null;
      this.lastFile = null;
      this.generating = false;
      this.clearRemoteState();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
      this.previewImage.removeAttribute('src');
      this.previewImage.alt = 'Your uploaded photo preview';
      delete this.preview.dataset.pmsPreviewKind;
      this.preview.hidden = true;
      this.section.querySelector('[data-pms-upload-trigger]').hidden = false;
      this.status.hidden = true;
      this.status.classList.remove('is-complete', 'is-error');
      this.generate.hidden = true;
      this.hideError();
      this.resetProgress();
      this.syncPurchaseGate();
    }

    clearRemoteState() {
      this.state = { generationId: '', originalUrl: '', previewUrl: '' };
      this.clearProperty('_pms_generation_id');
      this.clearProperty('_pms_original_image');
      this.clearPreviewProperties();
      this.removeGeneratedMedia();
      if (this.readyCard) this.readyCard.hidden = true;
      if (this.mobileView) this.mobileView.hidden = true;
      this.section.dataset.pmsPreviewReady = 'false';
      try {
        sessionStorage.removeItem(this.storageKey);
        sessionStorage.removeItem(this.legacyStorageKey);
      } catch {}
      this.syncPurchaseGate();
    }

    async start(file) {
      const validationError = this.validateFile(file);
      if (validationError) {
        this.showError(validationError, false);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      const revision = ++this.revision;
      this.generating = true;
      this.resetProgress();
      this.clearRemoteState();
      this.showLocalPreview(file);
      this.hideError();
      this.generate.hidden = false;
      this.syncPurchaseGate();

      try {
        this.setStage('Preparing upload', { floor: 2, ceiling: 10, duration: 2500 });
        const productId = String(this.section.dataset.pmsProductId || '');
        const variantId = this.currentVariantId();
        const prepared = await jsonFetch(`${this.apiUrl}/api/pms-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'pms.prepare-upload',
            payload: {
              mode: this.mode,
              product_id: productId,
              variant_id: variantId,
              mime_type: file.type
            }
          }),
          signal
        });
        this.assertCurrent(revision);
        if (!prepared?.ok || !isUuid(prepared.generation_id) || !prepared.pathname || !prepared.upload_grant) {
          throw new Error(prepared?.error || 'upload_authorization_failed');
        }

        const metadata = {
          mode: this.mode,
          mime_type: file.type,
          product_id: productId,
          variant_id: variantId,
          generation_id: prepared.generation_id,
          pathname: prepared.pathname,
          upload_grant: prepared.upload_grant
        };
        this.setStage('Uploading photo', { floor: 10, ceiling: 25, duration: 8000 });
        const blob = await uploadPresigned({
          apiUrl: this.apiUrl,
          pathname: prepared.pathname,
          file,
          metadata,
          signal
        });
        this.assertCurrent(revision);
        if (!isHttpsUrl(blob?.url) || blob.pathname !== prepared.pathname) throw new Error('invalid_blob_response');
        if (this.expectedBlobHostname && new URL(blob.url).hostname !== this.expectedBlobHostname) {
          throw new Error('unexpected_blob_hostname');
        }

        this.setStage('Creating your watercolor', { floor: 25, ceiling: 90, duration: 45000 });
        const generated = await jsonFetch(`${this.apiUrl}/api/pms-generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            generation_id: prepared.generation_id,
            original_url: blob.url,
            mode: this.mode,
            product_id: productId,
            variant_id: variantId
          }),
          signal
        });
        this.assertCurrent(revision);
        if (!generated?.ok || generated.generation_id !== prepared.generation_id || !isHttpsUrl(generated.preview_url)) {
          throw new Error(generated?.error || 'invalid_generation_response');
        }

        this.setStage('Finishing preview', { floor: 90, ceiling: 99, duration: 1500 });
        await this.complete({
          generationId: generated.generation_id,
          originalUrl: blob.url,
          previewUrl: generated.preview_url
        }, revision);
      } catch (error) {
        if (signal.aborted || revision !== this.revision) return;
        this.generating = false;
        this.showError(this.friendlyError(error), true);
      }
    }

    assertCurrent(revision) {
      if (revision !== this.revision || this.abortController?.signal.aborted) throw new DOMException('Aborted', 'AbortError');
    }

    async complete(state, revision = this.revision) {
      this.state = state;
      this.generating = false;
      this.setProperty('_pms_generation_id', state.generationId);
      this.setProperty('_pms_original_image', state.originalUrl);
      this.clearPreviewProperties();
      this.disableTeeinblueProperties();
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          productId: String(this.section.dataset.pmsProductId || ''),
          pathname: window.location.pathname,
          ...state
        }));
      } catch {}
      return this.markPreviewReady(state.previewUrl, revision);
    }

    isCurrentPreview(url, revision) {
      return isHttpsUrl(url) && revision === this.revision && url === this.state.previewUrl &&
        !this.listenerController.signal.aborted;
    }

    markPreviewReady(url, revision) {
      if (!this.isCurrentPreview(url, revision)) return false;
      this.generating = false;
      this.removeGeneratedMedia();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = '';
      this.previewImage.removeAttribute('src');
      this.previewImage.alt = 'Your uploaded photo preview';
      delete this.preview.dataset.pmsPreviewKind;
      this.preview.hidden = true;
      this.section.querySelector('[data-pms-upload-trigger]').hidden = true;
      this.generate.hidden = true;
      this.readyCard.hidden = false;
      this.mobileView.hidden = false;
      this.section.dataset.pmsPreviewReady = 'true';
      this.hideError();
      this.stopProgressAnimation();
      this.setProgress(100);
      this.status.hidden = true;
      this.status.classList.remove('is-error', 'is-complete');
      this.retry.hidden = true;
      this.syncPurchaseGate();
      console.info('[PMS]', { stage: 'complete', generation_id: this.state.generationId, status: 'ready' });
      return true;
    }

    restore() {
      let record = null;
      try { record = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch {}
      if (!record || record.productId !== String(this.section.dataset.pmsProductId || '') || record.pathname !== window.location.pathname) return false;
      if (!isUuid(record.generationId) || !isHttpsUrl(record.originalUrl) || !isHttpsUrl(record.previewUrl)) return false;
      this.complete(record, this.revision);
      return true;
    }

    setStage(stage, progress = null) {
      this.status.hidden = false;
      this.status.classList.remove('is-complete');
      this.status.classList.remove('is-error');
      this.statusText.textContent = stage;
      this.error.hidden = true;
      this.retry.hidden = true;
      if (progress) {
        this.setProgress(progress.floor);
        this.animateProgress(progress.ceiling, progress.duration);
      }
      console.info('[PMS]', { stage, status: 'working' });
    }

    stopProgressAnimation() {
      if (this.progressFrame) window.cancelAnimationFrame(this.progressFrame);
      this.progressFrame = 0;
    }

    resetProgress() {
      this.stopProgressAnimation();
      this.progressValue = 0;
      this.renderProgress();
    }

    setProgress(value) {
      const next = Math.max(this.progressValue, Math.min(100, Math.round(Number(value) || 0)));
      this.progressValue = next;
      this.renderProgress();
    }

    renderProgress() {
      if (!this.progress || !this.progressBar || !this.progressPercent) return;
      const value = Math.max(0, Math.min(100, this.progressValue));
      this.progress.setAttribute('aria-valuenow', String(value));
      this.progress.setAttribute('aria-valuetext', `${value}%`);
      this.progressBar.style.width = `${value}%`;
      this.progressPercent.textContent = `${value}%`;
    }

    animateProgress(target, duration) {
      this.stopProgressAnimation();
      const startValue = this.progressValue;
      const ceiling = Math.max(startValue, Math.min(99, target));
      const startedAt = performance.now();
      const tick = now => {
        const ratio = Math.min(1, (now - startedAt) / Math.max(1, duration));
        const eased = 1 - Math.pow(1 - ratio, 2);
        this.setProgress(startValue + ((ceiling - startValue) * eased));
        if (ratio < 1) this.progressFrame = window.requestAnimationFrame(tick);
        else this.progressFrame = 0;
      };
      this.progressFrame = window.requestAnimationFrame(tick);
    }

    hideError() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.retry.hidden = true;
    }

    showError(message, retryable) {
      this.stopProgressAnimation();
      this.generating = false;
      this.status.hidden = false;
      this.status.classList.remove('is-complete');
      this.status.classList.add('is-error');
      if (!this.statusText.textContent) this.statusText.textContent = 'Upload stopped';
      this.error.textContent = message;
      this.error.hidden = false;
      this.retry.hidden = !retryable;
      if (this.generate) this.generate.hidden = Boolean(retryable);
      this.syncPurchaseGate();
      console.info('[PMS]', { stage: 'error', status: 'failed' });
    }

    friendlyError(error) {
      const code = String(error?.message || 'generation_failed');
      if (code === 'origin_not_allowed') return 'This preview page is not allowed to upload yet. Please contact support.';
      if (code === 'unexpected_blob_hostname') return 'The upload destination could not be verified. Please try again later.';
      if (/file_too_large|larger than/i.test(code)) return 'Your photo is larger than 10 MB. Please choose a smaller file.';
      if (/unsupported_media_type/i.test(code)) return 'Please choose a JPEG, PNG or WebP image.';
      return 'We could not create your preview. Please try again.';
    }

    setProperty(property, value) {
      const input = this.form.querySelector(`[name="properties[${property}]"]`);
      if (!input) return;
      input.value = value;
      input.setAttribute('value', value);
      input.disabled = false;
    }

    clearProperty(property) {
      const input = this.form.querySelector(`[name="properties[${property}]"]`);
      if (!input) return;
      input.value = '';
      input.setAttribute('value', '');
      input.disabled = true;
    }

    clearPreviewProperties() {
      ['_pms_preview_ready', '_pms_preview_image'].forEach(property => {
        const input = this.form.querySelector(`[name="properties[${property}]"]`);
        if (!input) return;
        input.value = '';
        input.setAttribute('value', '');
        input.disabled = true;
      });
    }

    currentVariantId() {
      return String(this.form.querySelector('[name="id"]')?.value || '');
    }

    variantAvailable() {
      const id = this.currentVariantId();
      if (!id || this.form.querySelector('[name="id"]')?.disabled) return false;
      try {
        const variants = JSON.parse(this.section.querySelector('[data-fp-variants]')?.textContent || '[]');
        const variant = variants.find(item => String(item.id) === id);
        return variant ? Boolean(variant.available) : true;
      } catch { return true; }
    }

    ready() {
      return isUuid(this.state.generationId) && isHttpsUrl(this.state.originalUrl) && isHttpsUrl(this.state.previewUrl);
    }

    syncPurchaseGate() {
      const disabled = !this.variantAvailable() || this.submitting;
      this.buttons.forEach(button => {
        if (button.disabled !== disabled) button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        delete button.dataset.pmsPreviewLock;
      });
      const previewDisabled = disabled || !this.ready();
      this.viewButtons.forEach(button => {
        button.disabled = previewDisabled;
        button.setAttribute('aria-disabled', previewDisabled ? 'true' : 'false');
        button.textContent = this.submitting ? 'OPENING YOUR PREVIEW...' : 'VIEW MY PREVIEW';
      });
      if (this.generate) {
        this.generate.disabled = this.generating || this.submitting || !this.lastFile || this.ready();
        this.generate.setAttribute('aria-disabled', this.generate.disabled ? 'true' : 'false');
        this.generate.textContent = this.generating ? 'CREATING YOUR PREVIEW...' : 'GENERATE PREVIEW';
      }
    }

    disableTeeinblueProperties() {
      TEEINBLUE_PROPERTY_NAMES.forEach(name => {
        this.form.querySelectorAll(`[name="${name}"]`).forEach(input => { input.disabled = true; });
      });
    }

    interceptPurchase(event) {
      const button = event.type === 'click'
        ? event.target.closest?.('[data-fp-add-to-cart], [data-fp-checkout]')
        : event.submitter;
      const belongs = event.type === 'click'
        ? button && this.section.contains(button)
        : event.target === this.form;
      if (!belongs) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const checkoutRequested = Boolean(button?.matches('[data-fp-checkout]'));
      this.submitNormalToCart(checkoutRequested);
    }

    async submitNormalToCart(checkoutRequested = false) {
      if (this.submitting || !this.variantAvailable()) return;
      this.submitting = true;
      this.syncPurchaseGate();
      const error = this.section.querySelector('[data-fp-form-error]');
      error.hidden = true;
      error.textContent = '';
      this.disableTeeinblueProperties();

      const data = new FormData(this.form);
      TEEINBLUE_PROPERTY_NAMES.forEach(name => data.delete(name));
      data.set('id', this.currentVariantId());
      data.delete('properties[_pms_preview_ready]');
      data.delete('properties[_pms_preview_image]');
      if (this.ready()) data.delete('properties[_pms_photo_pending]');
      else if (this.lastFile || this.generating) data.set('properties[_pms_photo_pending]', 'true');
      else data.delete('properties[_pms_photo_pending]');

      try {
        const response = await jsonFetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data
        });
        if (response?.status) throw new Error(response.description || response.message || 'cart_error');
        window.location.assign(checkoutRequested ? '/checkout' : (window.routes?.cart_url || '/cart'));
      } catch (submitError) {
        error.textContent = submitError?.message || this.section.dataset.cartError || 'The item could not be added to your cart.';
        error.hidden = false;
        this.submitting = false;
        this.syncPurchaseGate();
      }
    }

    async submitPreviewToCart() {
      if (this.submitting || !this.ready() || !this.variantAvailable()) return;
      this.submitting = true;
      this.syncPurchaseGate();
      const error = this.section.querySelector('[data-fp-form-error]');
      error.hidden = true;
      error.textContent = '';
      this.disableTeeinblueProperties();
      const data = new FormData(this.form);
      TEEINBLUE_PROPERTY_NAMES.forEach(name => data.delete(name));
      data.set('id', this.currentVariantId());
      data.delete('properties[_pms_photo_pending]');
      data.set('properties[_pms_preview_ready]', 'true');
      data.set('properties[_pms_preview_image]', this.state.previewUrl);
      try {
        const response = await jsonFetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data
        });
        if (response?.status) throw new Error(response.description || response.message || 'cart_error');
        window.location.assign(window.routes?.cart_url || '/cart');
      } catch (submitError) {
        error.textContent = submitError?.message || this.section.dataset.cartError || 'The item could not be added to your cart.';
        error.hidden = false;
        this.submitting = false;
        this.syncPurchaseGate();
      }
    }

    removeGeneratedMedia() {
      this.section.querySelectorAll('[data-pms-gallery-slide], [data-pms-gallery-thumbnail]').forEach(node => node.remove());
      const firstSlide = this.section.querySelector('[data-fp-gallery-slide]');
      const firstThumbnail = this.section.querySelector('[data-fp-gallery-thumbnail]');
      if (firstSlide) firstSlide.hidden = false;
      if (firstThumbnail) firstThumbnail.setAttribute('aria-current', 'true');
    }
  }

  const mount = (scope = document) => {
    const sections = [];
    if (scope.matches?.('[data-pms-self-upload="true"]')) sections.push(scope);
    scope.querySelectorAll?.('[data-pms-self-upload="true"]').forEach(section => sections.push(section));
    sections.forEach(section => {
      if (instances.has(section)) return;
      const controller = new PmsUploadController(section);
      instances.set(section, controller);
      controller.mount();
    });
  };

  mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const section = event.target.matches?.('[data-pms-self-upload="true"]')
      ? event.target
      : event.target.querySelector?.('[data-pms-self-upload="true"]');
    if (!section) return;
    instances.get(section)?.destroy();
    instances.delete(section);
  });
})();
